import { cn } from "@/utils";
import { Button, Tooltip } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { AlertTriangle, Bell, Check } from "lucide-react";
import {
  formatNotificationDate,
  formatRelativeNotificationDate,
  NotificationItem,
} from "../services/notifications";
import { NotificationMessage } from "./NotificationMessage";

export const NotificationCard = ({
  notification,
  isMarkingAsRead,
  onMarkAsRead,
  closeDrawer,
}: {
  notification: NotificationItem;
  isMarkingAsRead: boolean;
  onMarkAsRead: (notificationId: number) => void;
  closeDrawer: () => void;
}) => {
  const isAlert = notification.type === "alert";
  const isUnread = !notification.isRead;
  const Icon = isAlert ? AlertTriangle : Bell;

  return (
    <div
      className={cn(
        "group bg-content1 rounded-large border-divider hover:bg-content2 border p-3 transition-colors",
        {
          "bg-primary-50/40 dark:bg-primary-50/5 hover:bg-primary-50/60 dark:hover:bg-primary-50/10":
            isUnread && !isAlert,
          "bg-danger-50/40 dark:bg-danger-50/5 hover:bg-danger-50/60 dark:hover:bg-danger-50/10":
            isUnread && isAlert,
        },
      )}
    >
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div
            className={cn(
              "rounded-medium flex h-9 w-9 items-center justify-center",
              {
                "bg-danger-100 text-danger": isAlert,
                "bg-primary-100 text-primary": !isAlert,
              },
            )}
            aria-hidden
          >
            <Icon size={16} />
          </div>
          {isUnread && (
            <span
              className={cn(
                "ring-content1 absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2",
                {
                  "bg-danger": isAlert,
                  "bg-primary": !isAlert,
                },
              )}
              aria-hidden
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={cn("text-foreground text-sm leading-tight", {
                "font-semibold": isUnread,
                "text-default-600 font-medium": !isUnread,
              })}
            >
              {notification.title}
            </h4>

            {isUnread && (
              <Tooltip content={t`Označi kot prebrano`} placement="left">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  radius="full"
                  className="-mt-1 -mr-1 h-6 w-6 min-w-6 shrink-0 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                  isDisabled={isMarkingAsRead}
                  onPress={() => onMarkAsRead(notification.id)}
                  aria-label={t`Označi kot prebrano`}
                >
                  <Check size={14} />
                </Button>
              </Tooltip>
            )}
          </div>

          <NotificationMessage
            notification={notification}
            onAfterNavigate={() => {
              if (!notification.isRead) {
                onMarkAsRead(notification.id);
              }
              closeDrawer();
            }}
          />

          <Tooltip
            content={formatNotificationDate(notification.createdAt)}
            placement="bottom-start"
          >
            <span className="text-default-400 mt-1 w-fit cursor-default text-[11px]">
              {formatRelativeNotificationDate(notification.createdAt)}
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
